import { html } from '@ensemble.music/ui'

/** Headline sizes, so the H2 of two neighbouring sections never carries the same weight. */
const sizes = {
  lg: 'text-4xl font-semibold leading-tight tracking-tight text-color max-md:text-3xl max-sm:text-2xl',
  md: 'text-3xl font-semibold tracking-tight text-color max-sm:text-2xl',
  sm: 'text-2xl font-semibold tracking-tight text-color max-sm:text-xl',
} as const

/**
 * The section head in three compositions: `stack` (title over lead), `split` (title and lead
 * side by side) and `center`. Deliberately not one fixed rhythm - each section picks its own
 * size and layout, so the page does not read as the same template five times.
 *
 * `kicker` is optional and renders as a quiet sentence-case line, never an all-caps label. Most
 * sections do without one.
 */
export function SectionHeading({
  kicker,
  title,
  lead,
  size = 'md',
  layout = 'stack',
}: {
  kicker?: string
  title: string
  lead?: string
  size?: keyof typeof sizes
  layout?: 'stack' | 'split' | 'center'
}) {
  const heading = (
    <html.div className="flex flex-col gap-3">
      {kicker ? <html.p className="text-base text-color-tertiary">{kicker}</html.p> : null}
      <html.h2 className={sizes[size]}>{title}</html.h2>
    </html.div>
  )

  if (layout === 'split') {
    return (
      <html.div className="flex flex-row flex-wrap items-end gap-10 w-full max-sm:gap-5">
        <html.div className="flex flex-col grow shrink basis-[440px] max-w-[640px]">
          {heading}
        </html.div>
        {lead ? (
          <html.p className="grow shrink basis-[280px] max-w-[400px] text-lg text-color-secondary">
            {lead}
          </html.p>
        ) : null}
      </html.div>
    )
  }

  if (layout === 'center') {
    return (
      <html.div className="flex flex-col items-center gap-4 w-full text-center">
        {heading}
        {lead ? (
          <html.p className="max-w-[620px] text-lg text-color-secondary">{lead}</html.p>
        ) : null}
      </html.div>
    )
  }

  return (
    <html.div className="flex flex-col gap-3 max-w-[680px]">
      {heading}
      {lead ? <html.p className="text-lg text-color-secondary">{lead}</html.p> : null}
    </html.div>
  )
}
